import type {RouteConfig, PathPattern} from './route.js';
import {
    combineCorsConfig,
    CorsConfig,
    CorsConfigSource,
    matchingCorsConfigSource,
    PERMIT_DEFAULT_CONFIG,
    validateCorsConfig
} from '../server/cors.js';
import {and, pattern, type ServerWebExchangeMatcher, upgradeMatcher} from '../server/util/matchers.js';
import {IOGateway} from '@interopio/gateway';

export function createCorsConfigSource(context: Pick<RouteConfig, 'sockets' | 'cors' | 'corsConfig'>): CorsConfigSource {
    const {sockets: routes, cors} = context;

    // create a default CORS config that allows all origins and methods and then combine it with the provided corsConfig
    const defaultCorsConfig: CorsConfig | undefined = context.corsConfig === false ? undefined : combineCorsConfig(PERMIT_DEFAULT_CONFIG, context.corsConfig);

    const validatedConfigs: Array<[ServerWebExchangeMatcher, CorsConfig | undefined]> = [];

    // for each socket route, combine the default CORS config, then any app CORS config that match, and finally the route CORS config
    for (const [path, route] of routes) {
        let routeCorsConfig: CorsConfig | undefined = defaultCorsConfig;
        for (const [matcher, config] of cors) {
            if (IOGateway.Filtering.valueMatches(matcher, path)) {
                if (config === undefined) {
                    routeCorsConfig = undefined;
                }
                else {
                    routeCorsConfig = routeCorsConfig === undefined ? config : combineCorsConfig(routeCorsConfig, config);
                }
            }
        }
        const config = context.corsConfig === false ? undefined : {
            allowOrigins: route.originFilters?.allow,
            allowMethods: ['GET', 'CONNECT', 'OPTIONS'],
            allowHeaders: [
                'Upgrade',
                'Connection',
                'Origin',
                'Sec-Websocket-Key',
                'Sec-Websocket-Version',
                'Sec-Websocket-Protocol',
                'Sec-Websocket-Extensions'
            ],
            exposeHeaders: ['Sec-Websocket-Accept', 'Sec-Websocket-Protocol', 'Sec-Websocket-Extensions'],
            allowCredentials: route.authorize?.access !== 'permitted' ? true : undefined,
        };
        routeCorsConfig = routeCorsConfig === undefined ? config : combineCorsConfig(routeCorsConfig, config);

        validatedConfigs.push([
            and([upgradeMatcher, pattern(path)]),
            validateCorsConfig(routeCorsConfig)]);
    }

    const appConfigs: Array<[PathPattern, CorsConfig | undefined]> = [];
    // add app CORS configs
    for (const [matcher, config] of cors) {
        let [, routeCorsConfig] = appConfigs.find(([m]) => String(m) === String(matcher)) ?? [matcher, defaultCorsConfig];

        routeCorsConfig = routeCorsConfig === undefined ? config : combineCorsConfig(routeCorsConfig, config);
        let added = false;
        for (const entry of appConfigs) {
            if (String(entry[0]) === String(matcher)) {
                entry[1] = routeCorsConfig;
                added = true;
                break;
            }
        }
        if (!added) {
            appConfigs.push([matcher, routeCorsConfig]);
        }
    }
    for (const [matcher, config] of appConfigs) {
        validatedConfigs.push([pattern(matcher), validateCorsConfig(config)]);
    }

    validatedConfigs.push([pattern(/\/api\/.*/), validateCorsConfig(defaultCorsConfig)]);


    return matchingCorsConfigSource({mappings: validatedConfigs});
}
